import React, { useState, useEffect } from "react";
import api from "../../../services/api";
import styles from "./AnalyticsDashboard.module.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Cell, LabelList
} from "recharts";

// функция для цвета по % выполнения
function getPerformanceColor(percent) {
  if (percent >= 90) return "green";
  if (percent >= 70) return "orange";
  return "red";
}

// Кастомный тултип (сбоку)
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const w = payload[0].payload;
    return (
      <div className={styles.tooltipBox}>
        <strong>{label}</strong>
        <p>Эффективность: {w.efficiency}%</p>
        <p>Маршрутов: {w.routes}</p>
        <p>Закрыто: {w.completed}</p>
        <p>Не доставлено: {w.failed}</p>
        <p>Открыто: {w.open}</p>
      </div>
    );
  }
  return null;
};

export default function AnalyticsDashboard() {
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [workers, setWorkers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [showStructure, setShowStructure] = useState(false);

  // загрузка отчета
  async function loadReport(from, to) {
    setLoading(true);
    try {
      const res = await api.post("/analytics", { from, to });
      const withEfficiency = res.map(w => ({
        ...w,
        efficiency: w.receiptsTotal > 0
          ? Math.round(((w.completed + w.failed) / w.receiptsTotal) * 100)
          : 0
      }));
      setWorkers(withEfficiency || []);
    } catch (err) {
      console.error("Ошибка загрузки аналитики", err);
    } finally {
      setLoading(false);
    }
  }

  // при первом заходе — отчёт за сегодня
  useEffect(() => {
    loadReport(today, today);
  }, []);

  // загрузка деталки
  async function loadDetail(worker) {
    try {
      const res = await api.post("/analytics/worker", {
        name: worker.name,
        from: fromDate,
        to: toDate,
      });
      const withEfficiency = {
        ...res,
        daily: res.daily.map(d => ({
          ...d,
          efficiency: d.completed + d.failed + d.open > 0
            ? Math.round(((d.completed + d.failed) / (d.completed + d.failed + d.open)) * 100)
            : 0
        }))
      };
      setDetail(withEfficiency);
    } catch (err) {
      console.error("Ошибка загрузки детализации", err);
    }
  }

  // сортировка сотрудников
  const sortedWorkers = [...workers].sort((a, b) =>
    sortOrder === "desc"
      ? b.efficiency - a.efficiency
      : a.efficiency - b.efficiency
  );

  const drivers = sortedWorkers.filter(w => w.role === "Водитель");
  const loaders = sortedWorkers.filter(w => w.role === "Грузчик");

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Аналитика сотрудников</h1>

      {/* Фильтры */}
      <div className={styles.filters}>
        <label>
          С:
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          По:
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button onClick={() => loadReport(fromDate, toDate)}>Сформировать отчёт</button>
        <button onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}>
          Сортировать: {sortOrder === "desc" ? "Лучшие сверху" : "Худшие сверху"}
        </button>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={showStructure}
            onChange={(e) => setShowStructure(e.target.checked)}
          />
          Показать структуру статусов
        </label>
      </div>

      {loading && <p>Загрузка...</p>}

      {!loading && workers.length > 0 && (
        <div className={styles.chartBox}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={sortedWorkers}
              margin={{ top: 20, right: 40, left: 20, bottom: 40 }}
              barSize={40}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" interval={0} angle={-30} textAnchor="end" height={60} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {showStructure ? (
                <>
                  <Bar dataKey="completed" stackId="a" fill="#28a745" name="Закрыто" />
                  <Bar dataKey="failed" stackId="a" fill="#dc3545" name="Не доставлено" />
                  <Bar dataKey="open" stackId="a" fill="#007bff" name="Открыто" />
                </>
              ) : (
                <Bar dataKey="efficiency" name="Эффективность">
                  <LabelList dataKey="efficiency" position="top" formatter={(val) => `${val}%`} />
                  {sortedWorkers.map((w, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={getPerformanceColor(w.efficiency)}
                    />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Водители */}
      <h2>Водители</h2>
      <div className={styles.cards}>
        {drivers.map(w => (
          <div key={w.name} className={styles.card} onClick={() => loadDetail(w)}>
            <h3>{w.name}</h3>
            <p>{w.role}</p>
            <p
              className={styles.percent}
              title={`Эффективность: ${w.efficiency}% • Маршрутов: ${w.routes}`}
            >
              {w.efficiency}% выполнения
            </p>
          </div>
        ))}
      </div>

      {/* Грузчики */}
      <h2>Грузчики</h2>
      <div className={styles.cards}>
        {loaders.map(w => (
          <div key={w.name} className={styles.card} onClick={() => loadDetail(w)}>
            <h3>{w.name}</h3>
            <p>{w.role}</p>
            <p
              className={styles.percent}
              title={`Эффективность: ${w.efficiency}% • Маршрутов: ${w.routes}`}
            >
              {w.efficiency}% выполнения
            </p>
          </div>
        ))}
      </div>

      {/* Модалка */}
      {detail && (
        <div className={styles.modalOverlay} onClick={() => setDetail(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2>{detail.name} — {detail.role}</h2>
            <p>Квитанций: {detail.receiptsTotal}</p>
            <p>Закрыто: {detail.completed}, Открыто: {detail.open}, Не доставлено: {detail.failed}</p>
            <p>Эффективность: {detail.efficiency}%</p>

            <div className={styles.chartBox}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={detail.daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="efficiency" stroke="#007bff" name="Эффективность" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <button onClick={() => setDetail(null)}>Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
}
